import Papa from "papaparse";
import * as XLSX from "xlsx";
import type { ImportRow } from "@guestbook/shared";

/** Result of reading a CSV / xlsx file: the header row plus the data rows. */
export interface ParsedFile {
  headers: string[];
  rows: string[][];
}

/** Dropdown value meaning "no CSV column feeds this field". */
export const NO_COLUMN = "__none__";

export interface ContactFieldOption {
  value: string;
  label: string;
}

/**
 * The mapping is keyed by app field; each value is the ordered list of CSV
 * column ids (stringified column indices) that feed it. More than one column
 * means the values are concatenated (e.g. a "First" + "Last" → Name merge).
 * An empty array (or one containing only NO_COLUMN) means the field is unmapped.
 */
export type ColumnMapping = Record<string, string[]>;

/**
 * The contact fields a column can map to. Dotted `address.*` values are nested
 * into an address object by `buildContactRows`; `tags` is comma-split. Keep in
 * sync with contactInputSchema in @guestbook/shared.
 */
export const CONTACT_FIELDS: ContactFieldOption[] = [
  { value: "name", label: "Name" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "address.line1", label: "Address line 1" },
  { value: "address.line2", label: "Address line 2" },
  { value: "address.city", label: "City" },
  { value: "address.stateName", label: "State / region" },
  { value: "address.postalCode", label: "ZIP" },
  { value: "address.countryName", label: "Country" },
  { value: "notes", label: "Notes" },
  { value: "tags", label: "Tags (comma-separated)" },
];

/**
 * Maps a normalized header alias → contact field. Contacts have a single
 * `name`, so first/last/full-name headers all map to it; a file with separate
 * first/last columns auto-maps both to Name (the user adjusts in the mapping
 * step if needed).
 */
const HEADER_ALIASES: Record<string, string> = {
  name: "name",
  fullname: "name",
  firstname: "name",
  first: "name",
  givenname: "name",
  fname: "name",
  lastname: "name",
  last: "name",
  surname: "name",
  familyname: "name",
  lname: "name",
  email: "email",
  emailaddress: "email",
  emailaddr: "email",
  phone: "phone",
  phonenumber: "phone",
  mobile: "phone",
  mobilephone: "phone",
  cell: "phone",
  cellphone: "phone",
  tel: "phone",
  telephone: "phone",
  address: "address.line1",
  addr: "address.line1",
  address1: "address.line1",
  addr1: "address.line1",
  addressline1: "address.line1",
  mailingaddress: "address.line1",
  street: "address.line1",
  street1: "address.line1",
  streetline1: "address.line1",
  streetaddress: "address.line1",
  streetaddress1: "address.line1",
  address2: "address.line2",
  addr2: "address.line2",
  addressline2: "address.line2",
  street2: "address.line2",
  streetline2: "address.line2",
  streetaddress2: "address.line2",
  apt: "address.line2",
  apartment: "address.line2",
  suite: "address.line2",
  unit: "address.line2",
  city: "address.city",
  town: "address.city",
  state: "address.stateName",
  region: "address.stateName",
  province: "address.stateName",
  zip: "address.postalCode",
  zipcode: "address.postalCode",
  zipcd: "address.postalCode",
  postal: "address.postalCode",
  postalcode: "address.postalCode",
  postcode: "address.postalCode",
  country: "address.countryName",
  notes: "notes",
  note: "notes",
  comments: "notes",
  comment: "notes",
  tags: "tags",
  tag: "tags",
  groups: "tags",
  labels: "tags",
};

function normalizeHeader(header: string): string {
  return header.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** Lowercased alphanumeric tokens of a header, e.g. "Home Zip Code" → [home, zip, code]. */
function tokenize(header: string): string[] {
  return header
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/** Levenshtein edit distance between two strings (iterative, single-row buffer). */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let prevDiag = prev[0]!;
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const next = Math.min(prev[j]! + 1, prev[j - 1]! + 1, prevDiag + cost);
      prevDiag = prev[j]!;
      prev[j] = next;
    }
  }
  return prev[b.length]!;
}

/** Normalized similarity in [0, 1]; 1 is identical. */
function similarity(a: string, b: string): number {
  const max = Math.max(a.length, b.length);
  return max === 0 ? 1 : 1 - levenshtein(a, b) / max;
}

// Below this similarity (or for alias keys shorter than this) we don't trust a
// fuzzy edit-distance match — it would invite noise like "id" ≈ "tag".
const FUZZY_MIN_SIMILARITY = 0.8;
const FUZZY_MIN_KEY_LENGTH = 4;

/**
 * Resolves one CSV header to a contact field, tolerant of real-world variation.
 * Order matters — earlier (higher-confidence) strategies win:
 *   1. exact alias on the fully normalized header;
 *   2. token pass: any header token, or adjacent-token join, that is an alias —
 *      preferring the longest matching key (so "zipcode" beats "zip", and
 *      "email address" → email rather than the "address" line);
 *   3. edit-distance fallback for typos ("Adress" → address → line1).
 * Returns null when nothing matches confidently.
 */
export function matchField(header: string): string | null {
  const normalized = normalizeHeader(header);
  if (!normalized) return null;
  if (HEADER_ALIASES[normalized]) return HEADER_ALIASES[normalized];

  // Build candidate keys: each token plus each adjacent-pair join.
  const tokens = tokenize(header);
  const candidates: string[] = [...tokens];
  for (let i = 0; i < tokens.length - 1; i++) {
    candidates.push(tokens[i]! + tokens[i + 1]!);
  }
  // Longest alias hit wins so more-specific keys take precedence.
  let best: { field: string; length: number } | null = null;
  for (const candidate of candidates) {
    const field = HEADER_ALIASES[candidate];
    if (field && (!best || candidate.length > best.length)) {
      best = { field, length: candidate.length };
    }
  }
  if (best) return best.field;

  // Typo-tolerant fallback against the alias keys.
  let bestFuzzy: { field: string; score: number } | null = null;
  for (const [key, field] of Object.entries(HEADER_ALIASES)) {
    if (key.length < FUZZY_MIN_KEY_LENGTH) continue;
    const score = similarity(normalized, key);
    if (score >= FUZZY_MIN_SIMILARITY && (!bestFuzzy || score > bestFuzzy.score)) {
      bestFuzzy = { field, score };
    }
  }
  return bestFuzzy?.field ?? null;
}

/** Human label for a column index (its header, or a positional fallback). */
export function columnLabel(parsed: ParsedFile, index: number): string {
  return parsed.headers[index]?.trim() || `Column ${index + 1}`;
}

/**
 * Best-effort auto-mapping of detected columns onto app fields. Returns a map
 * of app field → matching column ids. Multiple columns matching the same field
 * (e.g. a "First Name" and "Last Name" both resolving to Name) are kept in
 * order so they concatenate. Fields with no match get an empty array.
 */
export function guessMapping(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {};
  for (const field of CONTACT_FIELDS) mapping[field.value] = [];
  headers.forEach((header, index) => {
    const field = matchField(header);
    if (field && mapping[field]) mapping[field].push(String(index));
  });
  return mapping;
}

function parseCsv(text: string): ParsedFile {
  const result = Papa.parse<string[]>(text.trim(), { skipEmptyLines: true });
  const data = (result.data as unknown as string[][]) ?? [];
  const [headerRow, ...dataRows] = data;
  return {
    headers: (headerRow ?? []).map((h) => String(h ?? "").trim()),
    rows: dataRows.map((row) => row.map((cell) => String(cell ?? ""))),
  };
}

function parseXlsx(buffer: ArrayBuffer): ParsedFile {
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheetName = workbook.SheetNames[0];
  const sheet = sheetName ? workbook.Sheets[sheetName] : undefined;
  if (!sheet) return { headers: [], rows: [] };
  const data = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    blankrows: false,
    defval: "",
    raw: false,
  });
  const [headerRow, ...dataRows] = data;
  return {
    headers: ((headerRow as unknown[]) ?? []).map((h) => String(h ?? "").trim()),
    rows: (dataRows as unknown[][]).map((row) => row.map((cell) => String(cell ?? ""))),
  };
}

// File.text() / File.arrayBuffer() aren't implemented in jsdom (test env), so
// read via FileReader, which works in both the browser and jsdom.
function readAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsText(file);
  });
}

function readAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Parses a CSV or xlsx File into headers + data rows, dispatching on the file
 * extension. Throws if the file can't be read or has no header row.
 */
export async function parseFile(file: File): Promise<ParsedFile> {
  const name = file.name.toLowerCase();
  const parsed =
    name.endsWith(".xlsx") || name.endsWith(".xls")
      ? parseXlsx(await readAsArrayBuffer(file))
      : parseCsv(await readAsText(file));
  if (parsed.headers.length === 0) {
    throw new Error("No columns detected. Make sure the first row contains headers.");
  }
  return parsed;
}

/** Real (non-NO_COLUMN) column indices a field maps to, in order. */
function fieldColumns(mapping: ColumnMapping, field: string): number[] {
  return (mapping[field] ?? [])
    .filter((id) => id !== NO_COLUMN)
    .map((id) => Number(id));
}

/**
 * Applies an app field → column(s) mapping to the data rows, producing the
 * `{ rowNumber, data }` shape POST /imports expects. `rowNumber` is 1-based
 * with the header counted as row 1, so the first data row is row 2. Values from
 * merged columns are joined with a space; empty cells are dropped; tags are
 * comma-split; address.* fields are nested.
 */
export function buildContactRows(parsed: ParsedFile, mapping: ColumnMapping): ImportRow[] {
  return parsed.rows.map((row, index) => {
    const data: Record<string, unknown> = {};
    const address: Record<string, string> = {};

    for (const field of Object.keys(mapping)) {
      const columns = fieldColumns(mapping, field);
      if (columns.length === 0) continue;
      const values = columns
        .map((col) => String(row[col] ?? "").trim())
        .filter((v) => v !== "");
      if (values.length === 0) continue;

      if (field === "tags") {
        const tags = values.flatMap((v) =>
          v.split(",").map((t) => t.trim()).filter(Boolean),
        );
        if (tags.length > 0) data.tags = tags;
      } else if (field.startsWith("address.")) {
        address[field.slice("address.".length)] = values.join(" ");
      } else {
        data[field] = values.join(" ");
      }
    }

    if (Object.keys(address).length > 0) data.address = address;
    return { rowNumber: index + 2, data };
  });
}

/**
 * Flattens the mapping into the human-readable `field → source column(s)`
 * record stored with the import for history (e.g. `{ name: "First + Last" }`).
 */
export function toColumnMapping(parsed: ParsedFile, mapping: ColumnMapping): Record<string, string> {
  const out: Record<string, string> = {};
  for (const field of Object.keys(mapping)) {
    const columns = fieldColumns(mapping, field);
    if (columns.length > 0) {
      out[field] = columns.map((col) => columnLabel(parsed, col)).join(" + ");
    }
  }
  return out;
}
